import { GATEWAY_API_URL } from '@admin-panel/config/env';
import {
  ICreateRiderUserRequest,
  IDeactivateERPUserResponse,
  IRiderEmailPreviewResponse,
  IRiderManagementSearchRequest,
  IRiderUserEntry,
  IUpdateRiderUserRequest,
} from '@admin-panel/types';
import CrudApiClient from './crudapiclient';
import { axiosInstance } from './axios';

type LyskraftEnvelope<T> = {
  data?: T;
  success?: boolean;
  status?: {
    code?: number;
    key?: string;
    type?: string;
    message?: string;
  };
};

const unwrapEnvelope = <T>(envelope: LyskraftEnvelope<T>, fallbackMessage: string): T => {
  if (!envelope || envelope.success === false || envelope.status?.type === 'ERROR') {
    throw new Error(envelope?.status?.message || envelope?.status?.key || fallbackMessage);
  }
  if (envelope.data === undefined || envelope.data === null) {
    throw new Error(fallbackMessage);
  }
  return envelope.data;
};

class RiderManagementApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/identity/erpuser/rider-management');
  }

  async searchRiders(query: IRiderManagementSearchRequest): Promise<Array<IRiderUserEntry>> {
    const response = await axiosInstance.post(`${this.baseURL}/search`, query ?? {});
    return unwrapEnvelope<Array<IRiderUserEntry>>(response.data, 'Could not load riders') ?? [];
  }

  async previewEmail(payload: ICreateRiderUserRequest): Promise<IRiderEmailPreviewResponse> {
    const response = await axiosInstance.post(`${this.baseURL}/preview`, payload);
    return unwrapEnvelope<IRiderEmailPreviewResponse>(response.data, 'Could not preview rider email');
  }

  async createRider(payload: ICreateRiderUserRequest): Promise<IRiderUserEntry> {
    const response = await axiosInstance.post(`${this.baseURL}/`, payload);
    return unwrapEnvelope<IRiderUserEntry>(response.data, 'Could not create rider');
  }

  async updateRider(id: number, payload: IUpdateRiderUserRequest): Promise<IRiderUserEntry> {
    const response = await axiosInstance.put(`${this.baseURL}/${id}`, payload);
    return unwrapEnvelope<IRiderUserEntry>(response.data, 'Could not update rider');
  }

  async deactivateRider(id: number, payload: { reason: string }): Promise<IDeactivateERPUserResponse> {
    const response = await axiosInstance.post(`${this.baseURL}/${id}/deactivate`, payload);
    return unwrapEnvelope<IDeactivateERPUserResponse>(response.data, 'Could not deactivate rider');
  }
}

export default RiderManagementApiClient;
